import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {Link as RouterLink} from '@github-ui/react-router'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLBdi} from '@github-ui/safe-html/VerifiedHTML'
import {PencilIcon} from '@primer/octicons-react'
import {IconButton, Link as PrimerLink, PageHeader} from '@primer/react'
import {clsx} from 'clsx'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import {canonicalIssueUrl, issueReference, issueUrl} from '../../utils/urls'
import type {OptionConfig} from '../OptionConfig'
import type {HeaderViewerIssueNode, HeaderViewerRepositoryNode} from './header-viewer-query-types'
import {HeaderMenu, type HeaderMenuBaseProps} from './HeaderMenu'
import styles from './HeaderViewer.module.css'
import {ViewPositionIndicator} from './ViewPositionIndicator'

export type HeaderViewerFragment = Pick<HeaderViewerIssueNode, 'number' | 'titleHTML' | 'url'> & {
  readonly viewerCanUpdateNext: HeaderViewerIssueNode['viewerCanUpdateNext'] | undefined
  readonly repository: Pick<HeaderViewerRepositoryNode, 'name' | 'isArchived' | 'isWritable'> & {
    readonly owner: Pick<HeaderViewerRepositoryNode['owner'], 'login'>
  }
}

export type HeaderViewerProps = {
  headerViewerData: HeaderViewerFragment
  optionConfig: OptionConfig
} & HeaderMenuBaseProps

export function HeaderViewer({headerViewerData, optionConfig, ...rest}: HeaderViewerProps) {
  const {titleHTML, number, repository, url, viewerCanUpdateNext} = headerViewerData
  const canEditTitle = viewerCanUpdateNext && !optionConfig.isReadonly
  const fullPageIssueUrl = canonicalIssueUrl(
    issueUrl({owner: repository.owner.login, repo: repository.name, number}),
    optionConfig.accountIssueOption,
  )
  const issueNumberLabel = issueReference(number, url, optionConfig.accountIssueOption)

  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = getHovercardAttributesForType('issue', {
    owner: repository.owner.login,
    repo: repository.name,
    issueNumber: number,
  })

  const headerMenu = <HeaderMenu optionConfig={optionConfig} headerMenuData={headerViewerData} {...rest} />

  // Props here correspond to only having 'Copy link' button rendered in HeaderMenu
  const areHeaderActionsMinimal =
    !optionConfig.showIssueCreateButton && !optionConfig.additionalHeaderActions && !optionConfig.customEditMenuEntries

  const hasNavState = !!optionConfig.viewNavState
  // When the sticky header is enabled, the ViewPositionIndicator renders inside
  // the sticky top container in IssueViewer instead of here, to keep a single
  // navigation surface across the page.
  const showNavState = hasNavState && !isFeatureEnabled('issues_sticky_header')

  // With the sticky header on, HeaderMenu's actions relocate to the sticky bar, leaving its
  // menu empty. Mirror that logic to skip the PageHeader slots, else the empty ContextArea
  // still reserves a row and breaks header spacing.
  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')
  const relocateSidePanelNav = stickyHeaderEnabled && !!optionConfig.insideSidePanel
  const stickyHeaderMenuIsEmpty =
    stickyHeaderEnabled &&
    (relocateSidePanelNav ||
      (!optionConfig.additionalHeaderActions && !optionConfig.customEditMenuEntries && !optionConfig.onClose))

  return (
    <div aria-label="Header" role="region" data-testid={TEST_IDS.issueHeader} className={styles.headerRegion}>
      {showNavState && (
        <div className={styles.topBar}>
          <ViewPositionIndicator
            navigate={optionConfig.navigate}
            viewNavState={optionConfig.viewNavState}
            onLoadMore={optionConfig.onLoadMoreNavItems}
            onLoadBack={optionConfig.onLoadBackNavItems}
            pendingFocusDirection={optionConfig.viewNavState?.pendingFocusDirection}
            pendingNavSource={optionConfig.viewNavState?.pendingNavSource}
          />
          <div className={styles.topBarActions}>{headerMenu}</div>
        </div>
      )}
      <PageHeader
        className={clsx(
          styles.headerContainer,
          areHeaderActionsMinimal && styles.minimalHeader,
          isFeatureEnabled('issues_sticky_header') && styles.headerContainer_stickyHeader,
        )}
      >
        <PageHeader.TitleArea className={styles.PageHeader_TitleArea}>
          <PageHeader.Title
            as={'h1'}
            className={clsx(
              styles.PageHeader_Title,
              isFeatureEnabled('issues_sticky_header') && styles.PageHeader_Title_stickyHeader,
              optionConfig.useViewportQueries ? styles.PageHeader_Title_viewport : styles.PageHeader_Title_container,
              !optionConfig.insideSidePanel && styles.PageHeader_Title_marginRight,
            )}
          >
            <VerifiedHTMLBdi
              className={clsx(
                'markdown-title',
                styles.issueTitle,
                optionConfig.insideSidePanel && styles.issueTitle_marginRight,
              )}
              /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side */
              html={titleHTML as SafeHTMLString}
              data-testid={TEST_IDS.issueTitle}
            />
            <span className="d-inline-flex flex-items-center gap-1">
              {optionConfig.insideSidePanel ? (
                <div className={styles.issueNumberContainer}>
                  <PrimerLink
                    as={RouterLink}
                    to={fullPageIssueUrl}
                    inline
                    {...hovercardAttrs}
                    className={styles.issueNumberLink}
                  >
                    {issueNumberLabel}
                  </PrimerLink>
                </div>
              ) : (
                <span className={styles.issueNumberText}>
                  &nbsp;
                  {issueNumberLabel}
                </span>
              )}
              {canEditTitle && (
                <IconButton
                  icon={PencilIcon}
                  onClick={() => rest.setIsIssueTitleEditActive?.(true)}
                  size="medium"
                  aria-label={LABELS.editTitle}
                  description={LABELS.editTitle}
                  variant="invisible"
                  data-testid={TEST_IDS.editIssueTitleButton}
                  className={styles.editTitleButton}
                />
              )}
            </span>
          </PageHeader.Title>
        </PageHeader.TitleArea>
        {/* Mobile page viewport actions */}
        {!hasNavState && !stickyHeaderMenuIsEmpty && (
          <PageHeader.ContextArea>
            <PageHeader.ContextAreaActions className={styles.PageHeader_ContextAreaActions}>
              {headerMenu}
            </PageHeader.ContextAreaActions>
          </PageHeader.ContextArea>
        )}
        {/* Desktop page viewport, narrow container viewport actions */}
        {!hasNavState && !stickyHeaderMenuIsEmpty && !optionConfig.useViewportQueries && (
          // Only in the case where container-aware breakpoints are used
          <PageHeader.ContextArea hidden={false} className={styles.PageHeader_ContextArea}>
            <div className={styles.desktopMetadataContainer}>
              <div className={styles.metadataWrapper}>{headerMenu}</div>
            </div>
          </PageHeader.ContextArea>
        )}
        {!hasNavState && !stickyHeaderMenuIsEmpty && (
          <PageHeader.Actions
            className={clsx(
              styles.PageHeader_Actions,
              optionConfig.useViewportQueries
                ? styles.PageHeader_Actions_viewport
                : styles.PageHeader_Actions_container,
            )}
          >
            {headerMenu}
          </PageHeader.Actions>
        )}
      </PageHeader>
    </div>
  )
}
